<script setup lang="ts">
type Props = {
  options?: any[];
  disabled?: boolean;
  multiple?: boolean;
  openUp?: boolean;
  hasError?: boolean;
  optionsClass?: string;
  showFirstOnly?: boolean;
};

const {
  options = [],
  disabled,
  multiple,
  openUp,
  hasError,
  optionsClass,
  showFirstOnly = false,
} = defineProps<Props>();

const modelValue = defineModel<any[]>('modelValue', {
  default: [],
});
</script>

<template>
  <div
    :class="[
      $style.editableField,
      { [$style.isDisabled]: disabled },
      { [$style.hasError]: hasError },
    ]"
  >
    <HeadlessListbox
      v-model="modelValue"
      :class="$style.listBoxContainer"
      :disabled="disabled"
      :multiple="multiple"
    >
      <div class="relative">
        <HeadlessListboxButton :class="$style.listBoxButton">
          <p v-if="!modelValue.length"> {{ $t('general.select') }} </p>
          <p
            v-else
            class="block truncate max-w-28 md:max-w-40"
          >
            <template v-if="multiple && showFirstOnly">
              {{ modelValue[0]?.value }}
            </template>
            <template v-else>
              <span
                v-for="(item, i) in modelValue"
                :key="item.value"
              >
                <span v-if="i">, </span>
                {{ item.value }}
              </span>
            </template>
          </p>
          <NuxtIcon
            :class="$style.arrowIcon"
            name="arrows/angle_down"
          />
        </HeadlessListboxButton>

        <HeadlessListboxOptions
          :class="[
            $style.listBoxOptions,
            openUp ? 'bottom-0 -mb-3' : 'mt-3.5',
            optionsClass,
          ]"
        >
          <HeadlessListboxOption
            v-for="option in options"
            :key="option.value"
            v-slot="{ active, selected }"
            :class="$style.listBoxOption"
            :value="option"
          >
            <li
              :class="[
                active ? 'bg-primary/50 text-black font-medium' : 'text-texts',
                'relative cursor-default select-none py-2 pl-9 pr-4',
                $style.optionItem,
              ]"
            >
              <span
                :class="[
                  selected ? 'font-medium' : 'font-base',
                  'block truncate',
                ]"
              >
                {{ option.value }}
              </span>
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center pl-3"
              >
                <NuxtIcon
                  aria-hidden="true"
                  class="h-4 w-4"
                  name="check"
                />
              </span>
            </li>
          </HeadlessListboxOption>
        </HeadlessListboxOptions>
      </div>
    </HeadlessListbox>
  </div>
</template>

<style module lang="scss">
.editableField {
  @apply bg-auth-bg-inputs h-[46px] sm:h-[50px] rounded-md flex items-center relative;

  &.hasError {
    box-shadow: 0 0 200px inset rgba(255, 0, 0, 0.1);
  }

  &.isDisabled {
    @apply cursor-not-allowed opacity-50;

    &:hover {
      @apply opacity-50;
    }

    button,
    label,
    div {
      @apply cursor-not-allowed;
      &:hover {
        @apply opacity-100;
      }
    }
  }
}
.listBoxContainer {
  @apply relative inline-block text-left w-full;

  .listBoxOptions {
    @apply absolute min-w-32 w-full origin-bottom-right divide-y rounded-md bg-auth-bg-inputs ring-1 ring-black/5 focus:outline-none z-[999] overflow-auto max-h-[200px] p-1;

    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.75);
  }

  .listBoxButton {
    @apply inline-flex w-full px-4 items-center justify-between rounded-none text-xs md:text-sm font-medium disabled:cursor-not-allowed text-auth-text-inputs/50;

    .arrowIcon {
      @apply ml-auto opacity-30;
    }
  }

  .optionItem {
    @apply flex w-full items-center gap-1 rounded-md px-2 py-2 text-sm font-medium cursor-pointer text-auth-text-inputs;
  }
}
</style>
